import { useCallback, useRef, useState } from 'react';

import { useResizeObserver } from '@tanium/react-use-resize-observer';

type UseIsOverflowingType = [boolean, (node: HTMLElement | null) => void];

interface UseIsOverflowingProps {
  dimension?: 'width' | 'height';
  updateOnResize?: boolean;
}

/**
 * Hook for determining if an HTML Element has content that has overflowed its container.
 *
 * By default, it will check for overflowing width and will only set isOverflowing on the
 * initial render.
 *
 * This behavior can be adjusted via the `dimension` and `updateOnResize` props.
 *
 * @returns A tuple consisting of whether the element is overflowing and a ref to assign to the element
 */
const useIsOverflowing = (props?: UseIsOverflowingProps): UseIsOverflowingType => {
  const { dimension = 'width', updateOnResize = false } = props ?? {};

  const hasSetInitialValue = useRef(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateOverflowState = useCallback(
    (element: Element) => {
      setIsOverflowing(
        dimension === 'height'
          ? element.scrollHeight > element.clientHeight
          : element.scrollWidth > element.clientWidth,
      );
    },
    [dimension],
  );

  // set grid's current dimensions and coordinates each time they change
  const elementRef = useResizeObserver(
    useCallback(
      (e) => {
        // Update every time the element is resized
        if (updateOnResize || !hasSetInitialValue.current) {
          updateOverflowState(e.target);
          hasSetInitialValue.current = true;
        }
      },
      [updateOnResize, updateOverflowState],
    ),
  );

  return [isOverflowing, elementRef];
};

export default useIsOverflowing;
